// Near Miss admin screens, piece 6. Pure: data in, HTML out.
// Words: OBSERVATIONS_SCAN_WORDS.md rev 3 section 1. Mockup: MOCKUP_OBSERVATIONS_SCAN.html rev 2 (approved 2026-10-12).
// Built like the Work Requests screens: site first, Open or All, one record with its actions.

import { startedTextOf } from "./work-started";
import { nearMissRowsFor, picksText, pickPriority, type NearMissRow } from "./near-miss";
import { esc, ident } from "./training-screens";
import { priorityPill } from "./work-request-screens";

function row(label: string, value: string): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e"></div></div>`;
}

function usDay(iso: string): string {
  const [, m, d] = iso.split("-");
  return m && d ? `${m}/${d}` : "";
}

export function nearMissList(all: NearMissRow[], siteNames: Record<string, string>, site: string, openOnly: boolean, flash = ""): string {
  const shown = nearMissRowsFor(all, site, openOnly);
  const sites = Object.keys(siteNames).sort();
  const options = [
    `<option value="all"${site === "all" ? " selected" : ""}>All Sites</option>`,
    ...sites.map((x) => `<option value="${esc(x)}"${site === x ? " selected" : ""}>${esc(`${x} · ${siteNames[x]}`)}</option>`),
  ].join("");
  const base = `#/near-miss/${esc(site)}`;
  const chips = `<a class="em-chip${openOnly ? " on" : ""}" href="${base}">Open</a><a class="em-chip${openOnly ? "" : " on"}" href="${base}/all">All</a>`;
  const rows = shown
    .map((r) => {
      const line2 = [r.equipment, r.location].filter(Boolean).join(" · ");
      const line3 = [picksText(r.picks), r.name, usDay(r.createdOn)].filter(Boolean).join(" · ");
      return `<a class="ar gl-row em-row" href="#/near-miss/r/${encodeURIComponent(r.observationId)}">
        <span class="t"><b>${esc(`${r.observationId} · ${r.assetId}`)}</b>${line2 ? `<small>${esc(line2)}</small>` : ""}<small>${esc(line3)}</small>
        <span class="wr-pills">${priorityPill(r.priority)}<span class="pill ${r.status === "Open" ? "amber" : "grey"}">${esc(r.status)}</span>${r.workRequestId ? `<span class="pill grey">${esc(r.workRequestId)}</span>` : ""}</span></span></a>`;
    })
    .join("");
  const open = all.filter((r) => (site === "all" || r.siteNumber === site) && r.status === "Open").length;
  const title = site === "all" ? "All Sites" : siteNames[site] ?? `Site ${site}`;
  return `${ident("NM", `Near Miss · ${title}`, `${open} open`)}
    ${flash}
    <div class="em-ctrl">
      <label class="site-pick" for="nm-site">Site <select id="nm-site" data-act="nm-site">${options}</select></label>
      <span class="em-chips">${chips}</span>
    </div>
    <div class="box">${rows || `<div class="empty">${openOnly ? "No open near misses here." : "No near misses here yet."}</div>`}</div>
    <p class="ro">From the guest page. Open first, High at the top. Nobody was hurt; a near miss is a warning.</p>`;
}

export interface NearMissScreen {
  busy: boolean;
  error: string;
}

export function nearMissRecord(r: NearMissRow, siteName: string, backHref: string, st: NearMissScreen, flash = ""): string {
  const picks = r.picks.map((p) => row(p, priorityPill(pickPriority(p)))).join("");
  const reviewed =
    r.status === "Reviewed"
      ? `${row("Review", esc(r.reviewNote || "Reviewed"))}${row("Reviewed by", esc(`${r.reviewedByName}${r.reviewedAt ? ` · ${startedTextOf(r.reviewedAt)}` : ""}`))}`
      : "";
  const act =
    r.status === "Reviewed"
      ? ""
      : `<form id="nm-form" class="box" data-id="${esc(r.observationId)}">
      <div class="boxhd"><div class="nm"><b>Review It</b><small>Mark Reviewed needs one line. Make A Work Request puts the picks on a WR for this asset.</small></div></div>
      <div class="rows">
        <label class="r"><span class="k">What was done?</span><span class="v"><input class="fin wide" name="note" maxlength="300" placeholder="Required for Mark Reviewed" autocomplete="off"></span><span class="e"></span></label>
      </div>
      ${st.error ? `<div class="note red" role="status">${esc(st.error)}</div>` : ""}
      <div class="wr-acts">
        <button class="prim" type="submit" name="act" value="reviewed"${st.busy ? " disabled" : ""}>Mark Reviewed</button>
        <button class="prim wr-out" type="submit" name="act" value="workRequest"${st.busy ? " disabled" : ""}>Make A Work Request</button>
      </div>
    </form>`;
  return `<a class="back" href="${esc(backHref)}">Near Miss</a>
    ${ident("NM", `${r.observationId} · ${r.priority}`, [siteName, r.assetId, r.status].filter(Boolean).join(" · "))}
    ${flash}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>The Near Miss</b><small>${esc(r.kind)} from the SafeTag label, no sign-in.</small></div>${priorityPill(r.priority)}</div>
      <div class="rows">
        ${row("What happened", esc(r.note || "No line given"))}
        ${row("Equipment", esc(r.equipment || "Not on record"))}
        ${row("Location", esc(r.location || "Not on record"))}
        ${row("Asset ID", esc(r.assetId))}
        ${row("Reported by", esc(r.name))}
        ${row("Reported", esc(startedTextOf(r.createdAt)))}
        ${row("Status", esc(r.status))}
        ${r.workRequestId ? row("Work request", `<a class="lnk" href="#/work-requests/r/${encodeURIComponent(r.workRequestId)}">${esc(r.workRequestId)}</a>`) : ""}
        ${reviewed}
      </div>
    </div>
    <div class="box">
      <div class="boxhd"><div class="nm"><b>What Almost Happened</b><small>The first four picks are High.</small></div></div>
      <div class="rows">${picks || '<div class="empty">No picks.</div>'}</div>
    </div>
    ${act}
    <p class="ro">${r.status === "Reviewed" ? "Reviewed. Kept on record." : "Someone hurt is not a near miss. Those go to the supervisor and the injury process."}</p>`;
}
